import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Volume2, VolumeX, Lock } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { SEO } from "../components/SEO";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const playFlipSound = (() => {
  let ctx;
  return (muted) => {
    if (muted) return;
    try {
      ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
      const dur = 0.18;
      const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.2);
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.setValueAtTime(2400, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      src.connect(filter).connect(gain).connect(ctx.destination);
      src.start();
    } catch { /* audio blocked */ }
  };
})();

export default function ReaderPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const accessKey = params.get("key") || "";
  const [info, setInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [totalAllowed, setTotalAllowed] = useState(0);
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const bookRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    setPages([]); setLoading(true);
    api.get(`/reader/${slug}`, accessKey ? { key: accessKey } : {}).then(async (r) => {
      setInfo(r);
      const pdf = await pdfjsLib.getDocument(imgUrl(r.pdf_url)).promise;
      const allowed = r.full_access ? pdf.numPages : Math.min(r.preview_pages, pdf.numPages);
      setTotalAllowed(allowed);
      const rendered = [];
      for (let i = 1; i <= allowed; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.6 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        rendered.push(canvas.toDataURL("image/jpeg", 0.82));
        setPages([...rendered]);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug, accessKey]);

  const onFlip = useCallback((e) => {
    setCurrent(e.data);
    playFlipSound(mutedRef.current);
  }, []);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const flipPrev = () => { bookRef.current?.pageFlip()?.flipPrev(); };
  const flipNext = () => { bookRef.current?.pageFlip()?.flipNext(); };
  const jumpTo = (i) => { bookRef.current?.pageFlip()?.flip(i); playFlipSound(muted); };
  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else stageRef.current?.requestFullscreen?.();
  };

  const atPreviewEnd = info && !info.full_access && pages.length > 0 && current >= pages.length - 1;

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#E8E3D3]" data-testid="reader-page">
      <SEO title={`Read ${info?.title || "Magazine"}`} path={`/read/${slug}`} />
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to={`/magazines/${slug}`} className="text-sm text-[#B0B0B0] hover:text-[#D3C1A3]" data-testid="reader-back-link">← Back to {info?.title || "magazine"}</Link>
        <div className="flex items-center gap-2">
          <button onClick={() => setMuted(!muted)} data-testid="reader-mute-toggle" aria-label="Toggle sound"
            className="p-2 rounded-full hover:bg-[#3A342B] transition-colors duration-200">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button onClick={() => setZoom((z) => Math.max(0.6, z - 0.15))} data-testid="reader-zoom-out" aria-label="Zoom out" className="p-2 rounded-full hover:bg-[#3A342B]"><ZoomOut className="w-5 h-5" /></button>
          <button onClick={() => setZoom((z) => Math.min(2, z + 0.15))} data-testid="reader-zoom-in" aria-label="Zoom in" className="p-2 rounded-full hover:bg-[#3A342B]"><ZoomIn className="w-5 h-5" /></button>
          <button onClick={fullscreen} data-testid="reader-fullscreen" aria-label="Fullscreen" className="p-2 rounded-full hover:bg-[#3A342B]"><Maximize className="w-5 h-5" /></button>
        </div>
      </div>

      <div ref={stageRef} className="flipbook-stage relative py-6 bg-[#0F0F0F]" data-testid="flipbook-stage"
        style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
        {loading && <p className="text-center text-[#A0A0A0] py-24" data-testid="reader-loading">Preparing your magazine... {pages.length > 0 && `(${pages.length} pages ready)`}</p>}
        {!loading && pages.length === 0 && (
          <div className="text-center py-24" data-testid="reader-unavailable">
            <p className="font-display text-2xl font-semibold text-white">This edition couldn't be loaded</p>
            <p className="text-sm text-[#A0A0A0] mt-2">The digital issue is unavailable or the file couldn't be rendered.</p>
            <Link to="/magazines" data-testid="reader-browse-link" className="btn-primary mt-6">Browse Magazines</Link>
          </div>
        )}
        {pages.length > 0 && (
          <HTMLFlipBook ref={bookRef} width={420} height={560} size="stretch" minWidth={280} maxWidth={560}
            minHeight={380} maxHeight={760} maxShadowOpacity={0.5} showCover={true} mobileScrollSupport={true}
            onFlip={onFlip} className="mx-auto" data-testid="flipbook">
            {pages.map((src, i) => (
              <div key={i} className="bg-white">
                <img src={src} alt={`Page ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
              </div>
            ))}
          </HTMLFlipBook>
        )}

        {atPreviewEnd && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0F0F0F]/85 backdrop-blur-sm" data-testid="preview-limit-overlay">
            <div className="text-center max-w-sm mx-6 bg-white/5 border border-white/10 rounded-xl p-10">
              <Lock className="w-10 h-10 mx-auto text-[#D3C1A3] mb-4" />
              <h3 className="font-display text-2xl font-bold text-white">Enjoying the preview?</h3>
              <p className="text-sm text-[#B0B0B0] mt-3 leading-relaxed">
                You've reached the end of the free {info.preview_pages}-page preview. Subscribe to read the full issue and every future edition.
              </p>
              <Link to={`/magazines/${slug}`} data-testid="preview-subscribe-btn" className="btn-primary mt-6">
                Subscribe to Read Full Issue
              </Link>
            </div>
          </div>
        )}
      </div>

      {pages.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-10">
          <div className="flex items-center justify-center gap-4 mb-6">
            <button onClick={flipPrev} data-testid="reader-prev-btn" className="inline-flex items-center gap-1 border border-[#4A4438] rounded-full px-5 py-2 text-sm hover:border-[#D3C1A3] transition-colors duration-200">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-[#A0A0A0]" data-testid="reader-page-indicator">Page {Math.min(current + 1, totalAllowed)} / {totalAllowed}{!info?.full_access && " (preview)"}</span>
            <button onClick={flipNext} data-testid="reader-next-btn" className="inline-flex items-center gap-1 border border-[#4A4438] rounded-full px-5 py-2 text-sm hover:border-[#D3C1A3] transition-colors duration-200">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 justify-start" data-testid="reader-thumbnails">
            {pages.map((src, i) => (
              <button key={i} onClick={() => jumpTo(i)} data-testid={`reader-thumb-${i}`}
                className={`shrink-0 w-14 aspect-[3/4] rounded-sm overflow-hidden border-2 transition-colors duration-200 ${current === i ? "border-[#D3C1A3]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={src} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
